import { getPanel, tabLabelPanelId } from '../config/panelRegistry';

/**
 * Чистая часть настроек панелей: разбор ответа сервера, разрешение подписей и кэш первой
 * отрисовки.
 *
 * Модулем, а не внутри контекста, по той же причине, что `nodeCatalogCache.js` вынесен из
 * `useNodeCatalog.js`: компоненты в проекте юнит-тестами не покрываются, а разрешение подписей
 * и трёхзначная видимость проверять надо.
 */

export const PANEL_SETTINGS_STORAGE_KEY = 'callcenter::panelSettings';

/**
 * Ответ сервера → плоская карта переопределений. Всё, что не похоже на настройку, отбрасывается:
 * фронтенд новее или старше бэкенда не должен ронять интерфейс из-за лишнего поля.
 */
export const normalizeResponse = (payload) => {
  const panels = payload?.panels;
  if (!panels || typeof panels !== 'object') {
    return {};
  }

  const overrides = {};
  for (const [panelId, panel] of Object.entries(panels)) {
    if (!panel || typeof panel !== 'object') {
      continue;
    }

    const title = typeof panel.title === 'string' && panel.title.trim() !== '' ? panel.title : null;
    const description =
      typeof panel.description === 'string' && panel.description.trim() !== '' ? panel.description : null;
    const visible = typeof panel.visible === 'boolean' ? panel.visible : null;

    if (title !== null || description !== null || visible !== null) {
      overrides[panelId] = { title, description, visible };
    }
  }

  return overrides;
};

/** Название панели: переименование тенанта, иначе умолчание реестра. */
export const resolveTitle = (overrides, panelId) =>
  overrides?.[panelId]?.title ?? getPanel(panelId)?.title ?? '';

/**
 * Подпись панели. `null` — «подписи нет», и это не то же самое, что пустая строка: у части
 * панелей подпись динамическая и в реестре её нет вовсе.
 */
export const resolveDescription = (overrides, panelId) =>
  overrides?.[panelId]?.description ?? getPanel(panelId)?.description ?? null;

/**
 * Видимость панели. Трёхзначно, как на сервере: записи нет — решает реестр, есть — администратор.
 * Неизвестный id считается видимым — fail-open: устаревшая настройка не должна гасить панель,
 * которой в реестре уже нет.
 */
export const resolveVisibility = (overrides, panelId) => {
  const stored = overrides?.[panelId]?.visible;
  if (typeof stored === 'boolean') {
    return stored;
  }

  return getPanel(panelId)?.hiddenByDefault !== true;
};

/**
 * Название раздела в меню. `fallback` — подпись из `APP_TABS`: она приезжает вместе с самим
 * пунктом меню, и запрашивать её из реестра второй раз незачем.
 */
export const resolveTabLabel = (overrides, tabId, fallback) =>
  overrides?.[tabLabelPanelId(tabId)]?.title ?? fallback;

/**
 * Кэш первой отрисовки.
 *
 * Без него скрытая панель успевала бы мелькнуть до ответа сервера, а переименованная — прыгнуть
 * с умолчания на новое название. Это подсказка, а не источник правды: ответ сервера всегда
 * побеждает. Приём и обёртки try/catch — те же, что у `config/sidebarState.js`.
 */
export const readCachedPanelSettings = () => {
  try {
    const raw = localStorage.getItem(PANEL_SETTINGS_STORAGE_KEY);
    if (!raw) {
      return {};
    }

    return normalizeResponse({ panels: JSON.parse(raw) });
  } catch {
    return {};
  }
};

export const persistPanelSettings = (overrides) => {
  try {
    localStorage.setItem(PANEL_SETTINGS_STORAGE_KEY, JSON.stringify(overrides ?? {}));
  } catch {
    // no-op for environments without localStorage access
  }
};

export const clearCachedPanelSettings = () => {
  try {
    localStorage.removeItem(PANEL_SETTINGS_STORAGE_KEY);
    localStorage.removeItem(PANEL_LAYOUTS_STORAGE_KEY);
  } catch {
    // no-op for environments without localStorage access
  }
};

/**
 * Раскладки вкладок кэшируются отдельным ключом: формат `PANEL_SETTINGS_STORAGE_KEY` — плоский
 * словарь панелей, на нём стоят e2e-заготовки кэша, и вложить туда раскладки значило бы
 * сломать их. Без кэша вкладка на каждом входе прыгала бы с умолчания на сохранённую раскладку.
 */
export const PANEL_LAYOUTS_STORAGE_KEY = 'callcenter::panelLayouts';

const MAX_COLUMNS = 24;

const isNonNegativeInt = (value) => Number.isInteger(value) && value >= 0;

const normalizeLayoutItem = (item) => {
  if (!item || typeof item !== 'object' || typeof item.id !== 'string' || item.id.trim() === '') {
    return null;
  }

  const { x, y, w, h } = item;
  if (!isNonNegativeInt(x) || !isNonNegativeInt(y) || !Number.isInteger(w) || w < 1
      || !Number.isInteger(h) || h < 1) {
    return null;
  }

  return { id: item.id, x, y, w, h };
};

/**
 * `layouts` ответа → `{ [tab]: { columns, items, revision, updatedAtUtc } }`. Испорченная
 * вкладка отбрасывается целиком и рисуется по умолчанию, а не наполовину.
 */
export const normalizeLayouts = (layouts) => {
  if (!layouts || typeof layouts !== 'object') {
    return {};
  }

  const result = {};
  for (const [tab, layout] of Object.entries(layouts)) {
    if (!layout || typeof layout !== 'object' || !Array.isArray(layout.items)) {
      continue;
    }

    const { columns } = layout;
    if (!Number.isInteger(columns) || columns < 1 || columns > MAX_COLUMNS) {
      continue;
    }

    const items = layout.items.map(normalizeLayoutItem).filter(Boolean);
    result[tab] = {
      columns,
      items: items.filter((item) => item.x + item.w <= columns),
      revision: isNonNegativeInt(layout.revision) ? layout.revision : 0,
      updatedAtUtc: typeof layout.updatedAtUtc === 'string' ? layout.updatedAtUtc : null
    };
  }

  return result;
};

export const readCachedPanelLayouts = () => {
  try {
    const raw = localStorage.getItem(PANEL_LAYOUTS_STORAGE_KEY);
    return raw ? normalizeLayouts(JSON.parse(raw)) : {};
  } catch {
    return {};
  }
};

export const persistPanelLayouts = (layouts) => {
  try {
    localStorage.setItem(PANEL_LAYOUTS_STORAGE_KEY, JSON.stringify(layouts ?? {}));
  } catch {
    // no-op for environments without localStorage access
  }
};
